import React, { useMemo, useRef, useState } from 'react';
import { Modal, Button, notify } from 'app/components';
import { CaretLeft } from '@phosphor-icons/react';
import './style';
import { validateOtp } from './validation';
import OTPInput from 'react-otp-input';
import { OTP_LENGTH } from '../../../../constants/common';

const countMap = {
  0: 60,
  1: 180,
  2: 300,
  3: 420,
  4: 600,
};

const Otp = ({
  formData,
  isOpen,
  handlePrimaryClick,
  handleSecondaryClick,
  onResend,
  onEnterPress,
  errors,
  setErrors,
}) => {

  const [otpText, setOtpText] = useState('');

  const handleOtpChange = (otp) => {
    // Remove non-digit characters from the input value
    const otpCode = otp.replace(/\D/g, '');
    // Update form data with the new OTP and clear any error message
    setOtpText(otpCode);
    setErrors({ ...errors, otpError: '' });
    // Validate the OTP length
    if (otpCode.length === OTP_LENGTH && !validateOtp(otpCode)) {
      setErrors({ ...errors, otpError: 'Please enter correct OTP' });
    }
  };
  
  // Resend link count and timer state
  const [resendLinkCount, setIsResendLinkCount] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const interval = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const totalCount = useRef(0);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      onEnterPress(otpText);
    }
  };
  // Handle resend link click event.
  const onClickResend = () => {
    if (seconds === 0) {
      if (totalCount.current <= 5) {
        onResend();
        setIsLoading(true);
        setOtpText('');
        setErrors({ ...errors, otpError: '' });
      } else {
        notify('error', 'You have exceeded the resend otp limit!');
      }
      if (countMap[totalCount.current]) {
        setIsResendLinkCount((prev) => prev + 1);
        setSeconds(countMap[totalCount.current]);
        totalCount.current = totalCount.current + 1;
      }
    } else {
      console.log('Enter a code to proceed');
    }
  };

  // Use memoization to update the resend link count and timer
  useMemo(() => {
    if (resendLinkCount) {
      interval.current = setInterval(() => {
        if (seconds) setSeconds((prev) => prev - 1);
      }, 1000);
    } else clearInterval(interval.current);
  }, [resendLinkCount]);

  // Use memoization to update the resend label
  const resendLabel = useMemo(() => {
    if (seconds === 0) {
      setIsResendLinkCount(0);
      return 'Click to resend.';
    }
    if (resendLinkCount) {
      return `Resend in ${seconds} seconds`;
    }
    return 'Click to resend.';
  }, [resendLinkCount, seconds]);

  return (
    <div>
      <Modal
        isOpen={isOpen}
        onClickPrimaryCta={() => handlePrimaryClick(otpText)}
        onClickSecondaryCta={() => handleSecondaryClick({ clear: true })}
        primaryText="Confirm"
        disabled={otpText.length !== OTP_LENGTH}
        secondaryText="Use a different number"
        size="regular"
        onKeyDown={handleKeyDown}
        className={`otp-modal ${otpText.length !== OTP_LENGTH ? 'primary-cta.disabled' : ''}` }
        title={<div className='bg-grey-10 p-2 rounded-full'><CaretLeft size={16} 
          onClick={handleSecondaryClick}/></div>}
      >
        <>
          <div className="flex flex-row justify-start gap-6 heading-24 pb-3">
            {' '}
              Please enter your OTP
          </div>
          <div className="text-16">
              We’ve sent a {OTP_LENGTH} digit confirmation code to {formData.mobile}
          </div>
          <div  className= {`label-16 pt-8  block ${errors?.otpError ? 'text-[#D92D20]' : 'text-grey-70'}`}>
              OTP
          </div>
          <OTPInput
            value={otpText}
            onChange={handleOtpChange}
            isInputNum={true}
            numInputs={OTP_LENGTH}
            renderSeparator={null}
            onKeyDown={handleKeyDown}
            shouldAutoFocus={true}
            containerStyle="otp-container"
            inputStyle={`otp-input ${errors?.otpError ? 'error' : ''}`}
            renderInput={(props) => (
              <input {...props}
                inputMode="numeric"
                pattern="[0-9]*"
                type="tel"
                value={props.value} />
            )}
          />
          {errors?.otpError && (
            <span className="otp-helptext text-14 text-error">
              {errors?.otpError}
            </span>
          )}
          <span className="otp-helptext text-14 flex gap-1 pt-3">
              Didn’t get a code?
            <Button
              className={`font-bold resend-button ${seconds !== 0 ? ' resend-button.disabled' : 'text-slate-950 '}`}
              children={resendLabel}
              disabled={seconds !== 0 || totalCount.current > 5}
              type="tertiary-text-underlined"
              onClick={onClickResend}
            />
          </span>
        </>
      </Modal>
    </div>
  );
};

export default Otp;
